import { view } from "@risingstack/react-easy-state";
import { Alert, InputStepper } from "@tine/designsystem-ui-react";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import { useEffect, useRef, useState } from "react";

import { sendAddToCart, sendProductDetails } from "../../common/tracking";
import { LIST_TYPE } from "../../common/types/trackingTypes";
import { isDataAvailable } from "../../common/utils/asyncDataUtils";

import authStore from "../../stores/auth/authStore";
import loginState from "../../stores/auth/loginState";
import cartStore from "../../stores/cart/cartStore";
import productStore from "../../stores/product/productStore";
import searchStore from "../../stores/search/searchStore";
import uiStore from "../../stores/uiStore";

import Button from "../../components/Button";
import LeadTimeWarning from "../../components/LeadTimeWarning";

import theme from "../../themes/theme";
import DisplayPricePdp from "./DisplayPricePdp";

import { includes } from "lodash-es";
import { logger } from "../../common/logger";
import type { Product } from "../../common/types/productTypes";
import { formatUnit } from "../../common/utils";
import PricePerItem from "../../components/PricePerItem";
import { SEASONAL_PRODUCTS } from "../../stores/product/productUtils";
import ProductWarnings from "./ProductWarnings";
import StockDisplay from "./StockDisplay";
import VariableWeightWarning from "./VariableWeightWarning";

const listName = "Produktdetaljside";

const productSearchDomain = theme.searchConfig.searchDomains.find((searchDomain) => searchDomain.type === "product");

const algoliaIndex = productSearchDomain?.indexName;

type ProductDetailsInfoProps = {
   product: Product;
};

const ProductDetailsInfo = ({ product }: ProductDetailsInfoProps) => {
   const imageRef = useRef<HTMLImageElement>(null);
   const [amount, setAmount] = useState(1);

   const listInformation = {
      type: LIST_TYPE.CATEGORY,
      name: listName,
      length: 1
   };

   useEffect(() => {
      if (!isUndefined(product)) {
         if (loginState.is("NOT_LOGGED_IN")) {
            sendProductDetails(product);
         } else if (
            !isUndefined(productStore.stockLevels[product.sku]) &&
            isDataAvailable(productStore.stockLevels[product.sku])
         ) {
            const stock_status_number = productStore.stockLevels[product.sku]?.data ?? undefined;
            let stock_status: string | undefined;

            if (isUndefined(stock_status_number)) {
               stock_status = undefined;
            } else if (stock_status_number >= 10) {
               stock_status = "På lager";
            } else if (stock_status_number > 0) {
               stock_status = "Få på lager";
            } else {
               stock_status = "Ikke på lager";
            }
            sendProductDetails(product, stock_status);
         }
      }
   }, [loginState.currentState, product, product && productStore.stockLevels[product.sku]?.data]);

   const addToCart = async () => {
      if (!isNil(product)) {
         if (cartStore.addToCart(product.sku, amount)) {
            if (!isNil(imageRef)) {
               uiStore.addFlyingProduct(product.sku, imageRef);
            }
            sendAddToCart([{ sku: product.sku, qty: amount }], listInformation);
            if (algoliaIndex) {
               searchStore.reportAlgoliaAddToCart(product.sku, algoliaIndex);
            }
         } else {
            logger.warn("cart", "Error occured during add to cart");
         }
      }
   };

   const inStock = 0 !== productStore.stockLevels[product.sku]?.data;
   const loggedIn = authStore.isLoggedIn();
   const seasonalProduct = includes(SEASONAL_PRODUCTS, product.sku);

   return (
      <>
         <h2 className="tw-mb-1">{product.name}</h2>
         <div className="tw-mb-4">
            <div>
               Art.nr.: {product.sku} {product.epdNumber && `| EPD: ${product.epdNumber}`}
            </div>

            {!isNil(product.price) && (
               <div className="tw-flex tw-gap-1">
                  <span>{formatUnit(product.unit)}:</span>
                  <PricePerItem product={product} />
               </div>
            )}
         </div>
         {theme.productAvailability.enabled && !product.expired && <StockDisplay product={product} />}

         {loggedIn && <ProductWarnings product={product} />}

         {!product.expired && (
            <>
               <VariableWeightWarning product={product} />
               <LeadTimeWarning product={product} />
               <DisplayPricePdp product={product} />
               <div className="tw-flex md:tw-items-center md:tw-gap-2 tw-gap-4 md:tw-flex-row tw-flex-col">
                  <div className="tw-flex md:tw-mr-3">
                     <InputStepper key={product.sku} value={amount} min={1} onChange={setAmount} />
                  </div>
                  <div className="tw-mr-4">
                     {loggedIn ? (
                        <Button
                           disabled={!inStock}
                           variant="primary"
                           onClick={addToCart}
                           size="regular"
                           data-testid="add-to-cart-product-details-button"
                        >
                           {inStock ? "Legg i handlekurv" : "Ikke på lager"}
                        </Button>
                     ) : (
                        <Button variant="secondary" onClick={() => uiStore.showLoginModal()} size="regular">
                           Logg inn for kjøp
                        </Button>
                     )}
                  </div>
               </div>
            </>
         )}
         {product.expired && (
            <Alert variant="warning" showCloseButton={false}>
               {product.expired && (
                  <span>{seasonalProduct ? "Denne varen er utgått for sesongen" : "Denne varen er utgått fra sortimentet"}</span>
               )}
            </Alert>
         )}
      </>
   );
};

export default view(ProductDetailsInfo);
